import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader, DataTag } from "@/components/widgets";
import { Robot, Power, Warning, Play, ArrowClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import StockSignalsPanel from "@/components/StockSignalsPanel";

const FIELDS = [
  ["risk_per_trade_pct", "Risk per trade (% equity)"],
  ["stop_loss_pct", "Stop-loss (%)"],
  ["take_profit_pct", "Take-profit (%)"],
  ["max_position_pct", "Max position (% equity)"],
  ["max_sector_pct", "Max sector (% equity)"],
  ["max_open_positions", "Max open positions"],
  ["max_daily_loss_pct", "Daily loss halt (%)"],
  ["min_cash_pct", "Min cash reserve (%)"],
];

export default function AutoTrader() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [events, setEvents] = useState([]);
  const [preview, setPreview] = useState(null);
  const [scan, setScan] = useState(null);
  const [perf, setPerf] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, a, p] = await Promise.all([api.get("/auto/settings"), api.get("/auto/audit?limit=100"), api.get("/auto/performance")]);
    setSettings(s.data);
    setForm(s.data.config);
    setEvents(a.data.events);
    setPerf(p.data);
  }, []);
  useEffect(() => { load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const save = () => act(() => api.put("/auto/settings", { config: form }), "Risk settings saved");
  const toggle = (enabled) => act(() => api.put("/auto/settings", { enabled }),
    enabled ? "Auto-trading enabled (paper)" : "Auto-trading paused");
  const kill = () => {
    const flatten = window.confirm("Close ALL paper positions as well?\n\nOK = halt and close positions\nCancel = halt only");
    if (!window.confirm("Engage the kill switch? Auto-trading will stop immediately.")) return;
    act(() => api.post("/auto/kill", { flatten }), "Kill switch engaged");
  };
  const dryRun = async () => {
    setBusy(true);
    try {
      const r = (await api.post("/auto/run?dry_run=true")).data;
      setPreview(r.decisions || []);
      setScan(r.scan || null);
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!settings) return <Loader label="Loading auto-trader" />;
  const killed = settings.kill_switch;

  return (
    <div data-testid="autotrader-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-xs">Execution Layer</div>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter flex items-center gap-2">
            <Robot size={26} className="text-volt" /> Auto-Trader
          </h1>
        </div>
        <DataTag kind="HYPO" />
      </div>
      <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
        Runs your saved strategies on your watchlist every 5 minutes against the paper portfolio only.
        Entries are sized and gated by the risk limits below; every decision is logged. No live brokerage orders are placed.
        {" "}<Link to="/auto-fx" className="underline text-volt">Forex Auto-Trader →</Link>
      </p>

      {killed && (
        <div className="mt-4 border border-red-500/50 bg-red-500/10 p-4 flex items-center justify-between" data-testid="kill-banner">
          <div className="flex items-center gap-2 text-sm"><Warning size={18} /> Kill switch engaged — auto-trading is halted.</div>
          <Button variant="outline" className="rounded-sm" disabled={busy}
            onClick={() => act(() => api.post("/auto/kill/reset"), "Kill switch reset")}>Reset</Button>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-border bg-card p-5 flex flex-col gap-3">
          <div className="label-xs">Status</div>
          <div className="flex items-center gap-3">
            <Switch data-testid="auto-enable-switch" checked={settings.enabled} disabled={busy || killed}
              onCheckedChange={toggle} />
            <span className="text-sm">{settings.enabled ? "Running (paper)" : "Paused"}</span>
          </div>
          <div className="mono text-xs text-muted-foreground">
            Last run: {settings.last_run_at ? new Date(settings.last_run_at).toLocaleString() : "never"}
          </div>
          <div className="flex gap-2 mt-1">
            <Button data-testid="auto-dry-run" variant="outline" className="rounded-sm gap-2" disabled={busy || killed} onClick={dryRun}>
              <Play size={14} /> Preview run
            </Button>
            <Button data-testid="auto-kill" variant="outline" className="rounded-sm gap-2 border-red-500/60 text-red-400" disabled={busy} onClick={kill}>
              <Power size={14} /> Kill switch
            </Button>
          </div>
        </div>

        <div className="border border-border bg-card p-5 md:col-span-2">
          <div className="label-xs mb-3">Risk limits</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FIELDS.map(([k, label]) => (
              <label key={k} className="text-xs text-muted-foreground flex flex-col gap-1">
                {label}
                <Input data-testid={`risk-${k}`} type="number" step="any" value={form[k] ?? ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-sm" />
              </label>
            ))}
          </div>
          <Button data-testid="risk-save" className="mt-4 rounded-sm" disabled={busy} onClick={save}>Save limits</Button>
        </div>
      </div>

      {perf && (
        <div className="mt-5 border border-border bg-card p-5" data-testid="perf-box">
          <div className="label-xs mb-3">Realized performance (closed auto-trades)</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mono text-sm">
            <div><div className="text-xs text-muted-foreground">Trades</div>{perf.overall.trades}</div>
            <div><div className="text-xs text-muted-foreground">Win rate</div>{perf.overall.win_rate}%</div>
            <div><div className="text-xs text-muted-foreground">Total P&amp;L</div>${perf.overall.total_pnl}</div>
            <div><div className="text-xs text-muted-foreground">Avg return</div>{perf.overall.avg_return_pct}%</div>
            <div><div className="text-xs text-muted-foreground">Profit factor</div>{perf.overall.profit_factor ?? "—"}</div>
          </div>
          {Object.keys(perf.by_strategy).length > 0 && (
            <table className="w-full mono text-xs mt-4">
              <thead><tr className="text-left text-muted-foreground"><th className="py-1">Strategy</th><th>Trades</th><th>Win rate</th><th>P&amp;L</th><th>PF</th></tr></thead>
              <tbody>
                {Object.entries(perf.by_strategy).map(([name, s]) => (
                  <tr key={name} className="border-t border-border"><td className="py-1">{name}</td><td>{s.trades}</td><td>{s.win_rate}%</td><td>${s.total_pnl}</td><td>{s.profit_factor ?? "—"}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {preview && (
        <div className="mt-5 border border-border bg-card p-5" data-testid="preview-box">
          <div className="label-xs mb-2">Preview (nothing was traded)</div>
          {scan && (
            <div className="mono text-xs text-muted-foreground mb-2" data-testid="scan-summary">
              Scanned {scan.tickers} ticker(s) × {scan.strategies} strateg{scan.strategies === 1 ? "y" : "ies"} ({scan.scanned} checks), {scan.signals} fresh signal(s).
              {scan.strategies === 0 && " Save a strategy for alerts first."}
              {scan.tickers === 0 && " Add tickers to your watchlist first."}
            </div>
          )}
          {preview.length === 0 ? <div className="text-sm text-muted-foreground">No actions would be taken right now.</div> : (
            <ul className="mono text-xs space-y-1">
              {preview.map((d, i) => (
                <li key={i}>{d.action.toUpperCase()} {d.ticker} {d.quantity ? `x${d.quantity}` : ""} @ {d.price ?? "—"} — {d.reason}{d.strategy ? ` (${d.strategy})` : ""}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-5 border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="label-xs">Audit log</div>
          <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => load()}><ArrowClockwise size={13} /> Refresh</Button>
        </div>
        {events.length === 0 ? <div className="text-sm text-muted-foreground">No automated activity yet.</div> : (
          <table className="w-full mono text-xs">
            <thead><tr className="text-left text-muted-foreground"><th className="py-1">Time</th><th>Action</th><th>Ticker</th><th>Details</th></tr></thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-t border-border">
                  <td className="py-1 pr-3 whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                  <td className="pr-3">{e.action}</td>
                  <td className="pr-3">{e.ticker || "—"}</td>
                  <td className="text-muted-foreground">{Object.entries(e.detail || {}).map(([k, v]) => `${k}=${v}`).join("  ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <StockSignalsPanel />
    </div>
  );
}
